import { api } from "../../lib";

export type SystemEmailCategory =
  | "report_delivery"
  | "portal_access"
  | "portal_auth"
  | "staff_account"
  | "internal_ops";
export type SystemEmailMode = "on" | "review" | "off";
export type HeldEmailState =
  | "pending"
  | "blocked"
  | "sending"
  | "sent"
  | "cancelled"
  | "failed";

export interface SystemEmailRegistryEntry {
  id: string;
  label: string;
  category: SystemEmailCategory;
  subject: string;
  description: string;
  hasAttachment: boolean;
  holdable: boolean;
  mode: SystemEmailMode;
  revision: number;
  updatedAt: string | null;
  updatedByUserName: string | null;
}

export interface SystemEmailPreview {
  emailTypeId: string;
  subject: string;
  html: string;
}

export interface SystemEmailRegistryFilters {
  category?: SystemEmailCategory;
  mode?: SystemEmailMode;
}

export interface UpdateSystemEmailModeRequest {
  emailTypeId: string;
  mode: SystemEmailMode;
  expectedRevision: number;
}

export interface HeldEmailListItem {
  id: string;
  emailTypeId: string;
  emailTypeLabel: string;
  state: HeldEmailState;
  recipient: string;
  subject: string;
  caseId: string | null;
  caseNumber: string | null;
  createdAt: string;
  waitingForSeconds: number;
  releasedAt: string | null;
  releasedByUserName: string | null;
  failureReason: string | null;
}

export interface HeldEmailDetail extends HeldEmailListItem {
  bodyHtml: string;
  communicationLogId: string | null;
  cancelledAt: string | null;
  cancelledByUserName: string | null;
  reportDeliveryQueueId: string | null;
}

export interface HeldEmailStats {
  pending: number;
  blocked: number;
  sending: number;
  sent: number;
  cancelled: number;
  failed: number;
  total: number;
}

export interface HeldEmailFilters {
  states?: HeldEmailState[];
  emailTypeId?: string;
  caseId?: string;
  recipient?: string;
  page?: number;
  limit?: number;
}

export interface HeldEmailPage {
  data: HeldEmailListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface HeldEmailBulkResult {
  success: number;
  failed: number;
  errors: Array<{ heldEmailId: string; code: string; message: string }>;
}

function buildRegistryPath(filters: SystemEmailRegistryFilters): string {
  const searchParams = new URLSearchParams();
  if (filters.category) searchParams.set("category", filters.category);
  if (filters.mode) searchParams.set("mode", filters.mode);
  const query = searchParams.toString();
  return query ? `/system-emails?${query}` : "/system-emails";
}

function buildHeldEmailsPath(filters: HeldEmailFilters): string {
  const searchParams = new URLSearchParams();
  filters.states?.forEach((state) => searchParams.append("state", state));
  if (filters.emailTypeId) searchParams.set("emailTypeId", filters.emailTypeId);
  if (filters.caseId) searchParams.set("caseId", filters.caseId);
  if (filters.recipient) searchParams.set("recipient", filters.recipient);
  if (filters.page) searchParams.set("page", String(filters.page));
  if (filters.limit) searchParams.set("limit", String(filters.limit));
  const query = searchParams.toString();
  return query ? `/system-emails/held?${query}` : "/system-emails/held";
}

export const systemEmailsService = {
  getRegistry: async (
    filters: SystemEmailRegistryFilters = {}
  ): Promise<SystemEmailRegistryEntry[]> => {
    const response = await api.get<{ entries: SystemEmailRegistryEntry[] }>(
      buildRegistryPath(filters)
    );
    return response.entries;
  },
  getPreview: (emailTypeId: string): Promise<SystemEmailPreview> =>
    api.get<SystemEmailPreview>(`/system-emails/${emailTypeId}/preview`),
  updateMode: ({
    emailTypeId,
    ...request
  }: UpdateSystemEmailModeRequest): Promise<SystemEmailRegistryEntry> =>
    api.put<SystemEmailRegistryEntry>(
      `/system-emails/${emailTypeId}/mode`,
      request
    ),
  getHeldEmails: (filters: HeldEmailFilters = {}): Promise<HeldEmailPage> =>
    api.get<HeldEmailPage>(buildHeldEmailsPath(filters)),
  getHeldEmailStats: (): Promise<HeldEmailStats> =>
    api.get<HeldEmailStats>("/system-emails/held/stats"),
  getHeldEmail: (heldEmailId: string): Promise<HeldEmailDetail> =>
    api.get<HeldEmailDetail>(`/system-emails/held/${heldEmailId}`),
  releaseHeldEmails: (heldEmailIds: string[]): Promise<HeldEmailBulkResult> =>
    api.post<HeldEmailBulkResult>("/system-emails/held/release", {
      heldEmailIds,
    }),
  cancelHeldEmails: (heldEmailIds: string[]): Promise<HeldEmailBulkResult> =>
    api.post<HeldEmailBulkResult>("/system-emails/held/cancel", {
      heldEmailIds,
    }),
};
